import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isEmbeddedOdysseyApp } from '../../services/appEnvironment';

const DEFAULT_META = {
  title: 'OdysseyAI — Plan the trip. Know what to book next.',
  description: 'Turn one request into a practical day-by-day itinerary grounded in current web research, with clear sources and trustworthy booking handoffs.',
};

const NOT_FOUND_META = {
  title: 'Page not found · OdysseyAI',
  description: 'That OdysseyAI page could not be found. Return home or start a new trip plan.',
};

const ROUTE_META = {
  '/': DEFAULT_META,
  '/chat': {
    title: 'AI Trip Planner · OdysseyAI',
    description: 'Describe the trip you want and shape a grounded, day-by-day itinerary through conversation.',
  },
  '/trips': {
    title: 'My Trips · OdysseyAI',
    description: 'Open, refine, and manage the OdysseyAI trips saved to your account.',
  },
  '/account': {
    title: 'Manage Account · OdysseyAI',
    description: 'Manage your OdysseyAI account, request deletion, and check deletion status.',
  },
  '/upgrade': {
    title: 'Planning Credits · OdysseyAI',
    description: 'Add 100 OdysseyAI planning credits with a secure one-time Stripe purchase and no subscription.',
  },
  '/upgrade/thank-you': {
    title: 'Planning Credit Status · OdysseyAI',
    description: 'Check the status of your OdysseyAI planning credits. Payment and credit fulfillment require server confirmation.',
  },
  '/about': {
    title: 'About Sahel and OdysseyAI',
    description: 'Meet founder Sahel Mastoureshgh and learn why OdysseyAI makes thoughtful travel planning feel lighter.',
  },
  '/login': {
    title: 'Sign In · OdysseyAI',
    description: 'Sign in to save itineraries, sync planning credits, and continue trips across devices.',
  },
  '/legal/privacy': { title: 'Privacy Policy · OdysseyAI', description: 'How OdysseyAI collects, uses, protects, and retains personal information.' },
  '/legal/terms': { title: 'Terms of Use · OdysseyAI', description: 'Terms that apply when you use OdysseyAI travel planning services.' },
  '/legal/refund': { title: 'Refund Policy · OdysseyAI', description: 'OdysseyAI planning-credit refund eligibility and support process.' },
  '/legal/affiliate': { title: 'Affiliate Disclosure · OdysseyAI', description: 'How OdysseyAI may earn commission from optional travel-provider links.' },
  '/legal/disclaimer': { title: 'AI Travel Disclaimer · OdysseyAI', description: 'Important guidance for verifying AI-assisted travel plans before booking.' },
};

const updateMeta = (pathname) => {
  // SEO routes own their full metadata and JSON-LD through SeoHead. Keeping
  // this eager layout helper out of that path also keeps guide data code-split.
  if (pathname === '/trip-ideas' || pathname.startsWith('/trip-ideas/')) return;
  const meta = isEmbeddedOdysseyApp() && /^\/upgrade(?:\/|$)/i.test(pathname)
    ? { title: 'Continue Planning · OdysseyAI', description: 'Return to your planner and saved trips in OdysseyAI.' }
    : ROUTE_META[pathname] || NOT_FOUND_META;
  document.title = meta.title;
  document.querySelector('meta[name="description"]')?.setAttribute('content', meta.description);
  document.querySelector('link[rel="canonical"]')?.setAttribute(
    'href',
    `https://www.odesseyai.com${pathname === '/' ? '/' : pathname}`,
  );
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    updateMeta(pathname);
    if (hash) {
      const frame = window.requestAnimationFrame(() => {
        document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
      });
      return () => window.cancelAnimationFrame(frame);
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    return undefined;
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
